import React from "react";
import {Button, Card, Col, Form, Row} from "react-bootstrap";
import {GlobalContext} from "../../GlobalContext";
import {DateInput} from "../common/DateInput";
import {Glyph} from "../common/Glyph";
import {RadioList} from "../common/RadioList";

const WC_BODY_FIELDS = [
    'referralDate', 'apptDate', 'dateOfInjury', 'claimNumber', 'employer', 'approvedDx',
    'tpaName', 'adjusterName', 'adjusterPhone', 'adjusterFax',
    'nurseCaseManagerName', 'nurseCaseManagerCompany', 'nurseCaseManagerPhone', 'nurseCaseManagerFax',
    'attorneyName', 'attorneyPhone', 'attorneyFax',
    'servicesRequested', 'testingType', 'primaryMdName',
    'priorNeuroCare', 'priorNeuroName', 'referralReason', 'approvalStatus'
];

function wcBodyFromSheet(sheet) {
    const body = {};
    for (const field of WC_BODY_FIELDS) {
        body[field] = sheet[field] || '';
    }
    return body;
}

function fileNameFromObjectKey(objectKey) {
    if (!objectKey) {
        return 'document';
    }
    const parts = objectKey.split('/');
    return parts[parts.length - 1] || 'document';
}

function fileNameFromContentDisposition(header) {
    if (!header) {
        return null;
    }
    const match = /.*\sfilename="(.+)"/.exec(header);
    return match && match.length > 1 ? match[1] : null;
}

export default class WcDataSheetView extends React.Component {
    static contextType = GlobalContext;

    constructor(props) {
        super(props);
        this.state = {
            sheet: props.wcDataSheet,
            dirty: false,
            saving: false,
            supportingDocuments: []
        };
    }

    componentDidMount() {
        this.loadSupportingDocuments(this.props.wcDataSheet?.referralId);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.wcDataSheet !== this.props.wcDataSheet) {
            this.setState({sheet: this.props.wcDataSheet, dirty: false});
            const prevReferralId = prevProps.wcDataSheet?.referralId;
            const referralId = this.props.wcDataSheet?.referralId;
            if (prevReferralId !== referralId) {
                this.loadSupportingDocuments(referralId);
            }
        }
    }

    loadSupportingDocuments = (referralId) => {
        if (!referralId) {
            this.setState({supportingDocuments: []});
            return;
        }
        this.context.apiRequest('GET', `/referrals/${referralId}/supporting-documents`)
            .then(resp => {
                this.setState({supportingDocuments: resp?.data || []});
            })
            .catch(() => {
                this.setState({supportingDocuments: []});
            });
    };

    downloadDocument = (e, doc) => {
        e.preventDefault();
        let fileName = fileNameFromObjectKey(doc.objectKey);
        this.context.basicApiRequest('GET', `/supporting-documents/${doc.id}`)
            .then(res => {
                const disp = res.headers.get('Content-Disposition');
                fileName = fileNameFromContentDisposition(disp) || fileName;
                return res.blob();
            })
            .then(blob => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                link.click();
                URL.revokeObjectURL(url);
            })
            .catch(err => console.error(err));
    };

    fieldChanged = (e) => {
        const {name, value} = e.target;
        this.setState(prev => ({
            sheet: {...prev.sheet, [name]: value},
            dirty: true
        }));
    };

    radioChanged = (name, value) => {
        this.setState(prev => ({
            sheet: {...prev.sheet, [name]: value},
            dirty: true
        }));
    };

    cancel = () => {
        this.setState({sheet: this.props.wcDataSheet, dirty: false});
        if (this.props.onCancel) {
            this.props.onCancel();
        }
    };

    save = (e) => {
        e.preventDefault();
        const {referralId} = this.state.sheet;
        this.setState({saving: true});
        this.context.apiRequest("POST", `/referrals/${referralId}/wc-datasheet`, wcBodyFromSheet(this.state.sheet))
            .then(resp => {
                if (resp.data) {
                    const updated = {
                        ...this.state.sheet,
                        ...resp.data,
                        referralId
                    };
                    this.setState({sheet: updated, dirty: false, saving: false});
                    if (this.props.onSave) {
                        this.props.onSave(updated);
                    }
                } else {
                    this.setState({saving: false});
                }
            })
            .catch(() => this.setState({saving: false}));
    };

    renderDocumentsCard() {
        const {supportingDocuments} = this.state;
        if (!supportingDocuments.length) {
            return null;
        }
        return <Col md={6}>
            <Card>
                <Card.Header>Documents</Card.Header>
                <Card.Body className="py-2">
                    {supportingDocuments.map(doc =>
                        <div key={doc.id}>
                            <a href="#" onClick={e => this.downloadDocument(e, doc)}>
                                {fileNameFromObjectKey(doc.objectKey)}
                            </a>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </Col>;
    }

    renderPrintLink(d) {
        if (!d.id) {
            return null;
        }
        return <Row>
            <Col>
                <a href={`/viewReport/${encodeURIComponent('Workers Comp Data Sheet.rptdesign')}?pdf=true&wcDataSheetId=${d.id}`} target={"_blank"}>
                    <Glyph name={"printer"} className={"float-right clickable"} style={{fontSize: '1.5rem'}} label={"Print"}/></a>
            </Col>
        </Row>;
    }

    renderTopDateFields(d) {
        const hasDocuments = this.state.supportingDocuments.length > 0;
        return <Row className="mb-2">
            <Col md={hasDocuments ? 6 : 12}>
                <Form.Group>
                    <Form.Label size="sm">Referral Date</Form.Label>
                    <Form.Control size="sm" name="referralDate" value={d.referralDate || ''} onChange={this.fieldChanged}/>
                </Form.Group>
                <Form.Group>
                    <Form.Label size="sm">Appt Date</Form.Label>
                    <DateInput size="sm" name="apptDate" value={d.apptDate || ''} onChange={this.fieldChanged}
                               pattern={"^(0?[1-9]|1[0-2])\\/(0?[1-9]|1\\d|2\\d|3[01])\\/\\d{2,4}$"}
                               placeholder={"mm/dd/yy"}/>
                </Form.Group>
                <Form.Group>
                    <Form.Label size="sm">Date of Injury</Form.Label>
                    <DateInput size="sm" name="dateOfInjury" value={d.dateOfInjury || ''} onChange={this.fieldChanged}
                               pattern={"^(0?[1-9]|1[0-2])\\/(0?[1-9]|1\\d|2\\d|3[01])\\/\\d{2,4}$"}
                               placeholder={"mm/dd/yy"}/>
                </Form.Group>
            </Col>
            {this.renderDocumentsCard()}
        </Row>;
    }

    render() {
        const d = this.state.sheet || {};

        return <Form onSubmit={this.save}>
            {this.renderPrintLink(d)}
            {this.renderTopDateFields(d)}
            <Form.Row>
                <Form.Group as={Col} sm={4}>
                    <Form.Label size="sm">Claim #</Form.Label>
                    <Form.Control size="sm" name="claimNumber" value={d.claimNumber || ''} onChange={this.fieldChanged}/>
                </Form.Group>
                <Form.Group as={Col} sm={8}>
                    <Form.Label size="sm">Employer</Form.Label>
                    <Form.Control size="sm" name="employer" value={d.employer || ''} onChange={this.fieldChanged}/>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col} sm={6}>
                    <Form.Label size="sm">TPA Name</Form.Label>
                    <Form.Control size="sm" name="tpaName" value={d.tpaName || ''} onChange={this.fieldChanged}/>
                </Form.Group>
                <Form.Group as={Col} sm={6}>
                    <Form.Label size="sm">Approved DX</Form.Label>
                    <Form.Control size="sm" name="approvedDx" value={d.approvedDx || ''} onChange={this.fieldChanged}/>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col} sm={4}>
                    <Form.Label size="sm">Adjuster Name</Form.Label>
                    <Form.Control size="sm" name="adjusterName" value={d.adjusterName || ''} onChange={this.fieldChanged}/>
                </Form.Group>
                <Form.Group as={Col} sm={4}>
                    <Form.Label size="sm">Adjuster Phone</Form.Label>
                    <Form.Control size="sm" name="adjusterPhone" value={d.adjusterPhone || ''} onChange={this.fieldChanged}/>
                </Form.Group>
                <Form.Group as={Col} sm={4}>
                    <Form.Label size="sm">Adjuster Fax</Form.Label>
                    <Form.Control size="sm" name="adjusterFax" value={d.adjusterFax || ''} onChange={this.fieldChanged}/>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col} sm={6}>
                    <Form.Label size="sm">Nurse Case Manager</Form.Label>
                    <Form.Control size="sm" name="nurseCaseManagerName" value={d.nurseCaseManagerName || ''} onChange={this.fieldChanged}/>
                </Form.Group>
                <Form.Group as={Col} sm={6}>
                    <Form.Label size="sm">NCM Company</Form.Label>
                    <Form.Control size="sm" name="nurseCaseManagerCompany" value={d.nurseCaseManagerCompany || ''} onChange={this.fieldChanged}/>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col} sm={6}>
                    <Form.Label size="sm">NCM Phone</Form.Label>
                    <Form.Control size="sm" name="nurseCaseManagerPhone" value={d.nurseCaseManagerPhone || ''} onChange={this.fieldChanged}/>
                </Form.Group>
                <Form.Group as={Col} sm={6}>
                    <Form.Label size="sm">NCM Fax</Form.Label>
                    <Form.Control size="sm" name="nurseCaseManagerFax" value={d.nurseCaseManagerFax || ''} onChange={this.fieldChanged}/>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col} sm={4}>
                    <Form.Label size="sm">Attorney Name</Form.Label>
                    <Form.Control size="sm" name="attorneyName" value={d.attorneyName || ''} onChange={this.fieldChanged}/>
                </Form.Group>
                <Form.Group as={Col} sm={4}>
                    <Form.Label size="sm">Attorney Phone</Form.Label>
                    <Form.Control size="sm" name="attorneyPhone" value={d.attorneyPhone || ''} onChange={this.fieldChanged}/>
                </Form.Group>
                <Form.Group as={Col} sm={4}>
                    <Form.Label size="sm">Attorney Fax</Form.Label>
                    <Form.Control size="sm" name="attorneyFax" value={d.attorneyFax || ''} onChange={this.fieldChanged}/>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col} sm={6}>
                    <Form.Label size="sm">Services Requested</Form.Label>
                    <Form.Control size="sm" name="servicesRequested" value={d.servicesRequested || ''} onChange={this.fieldChanged}/>
                </Form.Group>
                <Form.Group as={Col} sm={6}>
                    <Form.Label size="sm">Testing Type</Form.Label>
                    <Form.Control size="sm" name="testingType" value={d.testingType || ''} onChange={this.fieldChanged}/>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col} sm={6}>
                    <Form.Label size="sm" className="d-block">Prior Neuro?</Form.Label>
                    <RadioList className="d-block" name="priorNeuroCare" values={['Y', 'N']} currentValue={d.priorNeuroCare}
                               onChange={e => this.radioChanged('priorNeuroCare', e.target.value)}/>
                </Form.Group>
                <Form.Group as={Col} sm={6}>
                    <Form.Label size="sm" className="d-block">Approval</Form.Label>
                    <RadioList className="d-block" name="approvalStatus" values={['Approved', 'Denied']} currentValue={d.approvalStatus}
                               onChange={e => this.radioChanged('approvalStatus', e.target.value)}/>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col} sm={12}>
                    <Form.Label size="sm">Referral Reason</Form.Label>
                    <Form.Control as="textarea" rows={2} size="sm" name="referralReason" value={d.referralReason || ''} onChange={this.fieldChanged}/>
                </Form.Group>
            </Form.Row>
            <Row>
                <Col className="text-right">
                    <Button variant="secondary" onClick={this.cancel}>Cancel</Button>
                    <Button variant="dark" type="submit" className="ml-2" disabled={!this.state.dirty || this.state.saving}>Save</Button>
                </Col>
            </Row>
        </Form>;
    }
}
